import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi } from "../../lib/portalApi";
import { toast } from "sonner";
import { Trash2, UserPlus, Search, Save } from "lucide-react";

const EMPTY = {
  name: "", organization: "", role: "", email: "", phone: "",
  website: "", city: "", county: "", state: "",
  lead_source: "", interest_category: "", status: "New Lead",
  notes: "", assigned_rep: "", follow_up_date: "",
};

export default function CrmTab() {
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const r = await adminApi.crmList(q || undefined, statusFilter || undefined);
    setRows(r.contacts || []);
    setStatuses(r.statuses || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminApi.crmUpdate(editing.id, form);
        toast.success("Contact updated");
      } else {
        await adminApi.crmCreate(form);
        toast.success("Contact created");
      }
      setShowForm(false); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await adminApi.crmDelete(id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-crm-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 flex gap-2 min-w-[240px]">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name/org/email"
            className="bg-navy-deep border-gold-dim/40" data-testid="crm-search" />
          <Select value={statusFilter || "__all"}
            onValueChange={v => setStatusFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-56" data-testid="crm-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              <SelectItem value="__all">All statuses</SelectItem>
              {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} className="btn-ghost-gold h-10 px-4 text-xs" data-testid="crm-search-btn">
            <Search size={14} />
          </Button>
        </div>
        <Button onClick={openNew} className="btn-gold h-10 px-5 text-xs" data-testid="crm-new-btn">
          <UserPlus size={14} className="mr-2" /> New Contact
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="glass p-5 grid md:grid-cols-3 gap-3" data-testid="crm-form">
          <Input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" data-testid="crm-name" />
          <Input placeholder="Organization" value={form.organization || ""} onChange={e => setForm({ ...form, organization: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Role" value={form.role || ""} onChange={e => setForm({ ...form, role: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input type="email" placeholder="Email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" data-testid="crm-email" />
          <Input placeholder="Phone" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Website" value={form.website || ""} onChange={e => setForm({ ...form, website: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="City" value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="County" value={form.county || ""} onChange={e => setForm({ ...form, county: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="State" value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Lead Source" value={form.lead_source || ""} onChange={e => setForm({ ...form, lead_source: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Interest Category" value={form.interest_category || ""} onChange={e => setForm({ ...form, interest_category: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="crm-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Assigned Rep" value={form.assigned_rep || ""} onChange={e => setForm({ ...form, assigned_rep: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Follow-up date (YYYY-MM-DD)" value={form.follow_up_date || ""}
            onChange={e => setForm({ ...form, follow_up_date: e.target.value })}
            className="bg-navy-deep border-gold-dim/40" />
          <div className="md:col-span-3">
            <Textarea rows={3} placeholder="Notes" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })}
              className="bg-navy-deep border-gold-dim/40 text-cream" />
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <Button type="button" onClick={() => setShowForm(false)} className="btn-ghost-gold h-10 px-4 text-xs">Cancel</Button>
            <Button type="submit" className="btn-gold h-10 px-5 text-xs" data-testid="crm-save-btn">
              <Save size={14} className="mr-2" /> {editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      )}

      <div className="glass p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-table">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-steel">
            <tr className="border-b border-gold-dim/20">
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Organization</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Rep</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-gold-dim/10 hover:bg-navy-soft/40">
                <td className="p-4 text-gold">{r.name}</td>
                <td className="p-4 text-cream">{r.organization || "—"}</td>
                <td className="p-4 text-cream-dim font-mono text-xs">{r.email || "—"}</td>
                <td className="p-4 text-electric text-xs uppercase tracking-wider">{r.status}</td>
                <td className="p-4 text-cream-dim">{r.assigned_rep || "—"}</td>
                <td className="p-4 text-right flex justify-end gap-3">
                  <button onClick={() => openEdit(r)} className="text-electric hover:text-gold text-xs"
                    data-testid={`crm-edit-${r.id}`}>Edit</button>
                  <button onClick={() => del(r.id, r.name)} className="text-[color:var(--auditiq-critical)] hover:text-gold"
                    data-testid={`crm-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="p-10 text-center text-cream-dim italic">No contacts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
